import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSyncPendingPatientFiles } from '../../hooks';
import { useCompanyStore, useAnswersStore } from '../../stores';
import { NavigationUtils, WindowDimensionsUtils } from '../../utils';
import { getAttendanceFiles } from '../../utils/patientFiles';
import { createAttendanceWithLink } from '../../utils/scheduleLinkAttendance';
import { list } from '../../processes/calendar';
import { listEvents } from '../../processes/agenda-event';
import SelectTimegridForm from './SelectTimegridForm';
import moment from 'moment';
import { showErrorToast } from '@amigoapp/doca-react';

const footerPlusHeaderHeight = 344;

function SelectTimegridFormContainer() {
	const history = useHistory();
	const { companySettings: { settings, scheduleSettings } } = useCompanyStore();
	const { answers, setAnswer, clearEventSlotPlaceAndUser, appendAnswers } = useAnswersStore();
	const { syncPatientFiles } = useSyncPendingPatientFiles();

	const dimensions = WindowDimensionsUtils.useWindowDimensions();

	const [loading, setLoading] = useState(true);
	const [loadingTimegrid, setLoadingTimegrid] = useState(false);
	const [slotsByDate, setSlotsByDate] = useState([]);
	const [enabledDates, setEnabledDates] = useState([]);
	const [currentSlotsByUser, setCurrentSlotsByUser] = useState([]);
	const [events, setEvents] = useState([]);
	const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));

	const listMaxHeight = useMemo(() => (!dimensions?.height ? 300 : dimensions?.height - footerPlusHeaderHeight), [dimensions?.height]);

	const getEvents = useCallback(async () => {
		setLoading(true);

		const filter = {
			insurance_id: answers?.insurance,
			scheduler_event_group_id: answers?.event_group?.id
		};

		if (!filter.scheduler_event_group_id) {
			setLoading(false);
			return;
		}

		const { data, error } = await listEvents(filter);

		if (error) {
			const errorMessage = error?.code === 403
				? 'Sua credencial expirou, valide seu número novamente'
				: 'Não foi possível buscar os eventos.';

			showErrorToast(errorMessage);
		} else {
			setEvents(data);
		}
		setLoading(false);
	}, [answers?.insurance, answers?.event_group?.id]);

	const getCalendarAndSlots = useCallback(
		async (selectedEvent, selectedPlace) => {
			const filter = {
				event_id: selectedEvent?.id,
				place_id: selectedPlace?.id,
				insurance_id: answers?.insurance,
				patient_id: answers?.patient?.id,
				born: answers?.patient?.born,
				gender: answers?.patient?.gender
			};

			if (!filter.event_id || !filter.place_id) return;

			setLoadingTimegrid(true);
			const { data, error } = await list(filter);

			if (error) {
				showErrorToast('Não foi possível buscar os horários disponíveis.');
			}

			const { datesEnabled, currentDateSlotsByUser, closestSelectedDate } = (data || []).reduce(
				(acc, info) => {
					if (!acc.closestSelectedDate && info.status === 'AVAILABLE') {
						acc.closestSelectedDate = info.date;
						acc.currentDateSlotsByUser = info.slotsByUser;
					}
					if (info.status === 'AVAILABLE') {
						acc.datesEnabled.push(info.date);
					}
					return acc;
				},
				{
					datesEnabled: [],
					currentDateSlotsByUser: {},
					closestSelectedDate: null
				}
			);

			setSlotsByDate(data);
			setEnabledDates(datesEnabled);
			setCurrentSlotsByUser(currentDateSlotsByUser);
			setSelectedDate(closestSelectedDate || moment().format('YYYY-MM-DD'));
			setLoadingTimegrid(false);
		},
		[answers?.insurance, answers?.patient?.id, answers?.patient?.born, answers?.patient?.gender]
	);

	useEffect(() => {
		getEvents();
	}, [getEvents]);

	useEffect(() => {
		if (answers?.event && answers?.place) {
			getCalendarAndSlots(answers?.event, answers?.place);
		}
	}, [answers?.event, answers?.place, getCalendarAndSlots]);

	const handleSubmit = useCallback(async () => {
		const { event, place, insurance, slot } = answers;

		if (!event || !place || !slot) {
			showErrorToast('Por favor, complete todos os campos.');
			return;
		}

		const start = moment(slot.start, 'HH:mm');
		const end = moment(slot.end, 'HH:mm');
		const startDate = moment(selectedDate).hour(start.format('HH')).minute(start.format('mm'));
		const endDate = moment(selectedDate).hour(end.format('HH')).minute(end.format('mm'));

		const attendanceData = {
			event_id: event.id,
			place_id: place.id,
			patient_id: answers?.patient?.id,
			insurance_id: insurance,
			start_date: startDate,
			end_date: endDate,
			user_id: slot.user_id,
			insurance_number: answers?.insurance_number
		};

		if (!answers.patient?.id) {
			appendAnswers({
				slot: slot,
				start_date: startDate,
				end_date: endDate
			});
			return NavigationUtils.navigate(history, '/patient-form');
		}

		setLoading(true);

		const { error: uploadError, insuranceCardFile, otherDocumentFile, patientFiles } = await syncPatientFiles(answers.patient.id, answers?.patient_files);

		if (uploadError) {
			setLoading(false);
			return;
		}

		const { insuranceCardFileId, filesIds } = getAttendanceFiles({ insuranceCardFile, otherDocumentFile, patientFiles });

		const { data, error, linkError } = await createAttendanceWithLink({
			attendanceData: {
				...attendanceData,
				insurance_card_file_id: insuranceCardFileId,
				files_ids: filesIds
			},
			scheduleLinkCode: answers.schedule_link_code,
			setAnswer,
			history
		});
		setLoading(false);

		if (error) {
			if (linkError) {
				return;
			}

			error.code === 'ATTENDANCE_LIMIT_REACHED'
				? showErrorToast('Limite de agendamentos para esse CPF atingido. Tente novamente em outra data.')
				: showErrorToast('Algo de errado aconteceu ao criar o agendamento.');
		} else {
			appendAnswers({ external_pages_token: data.external_pages_token });

			if (settings?.is_anesthetist_company) {
				localStorage.setItem('token', data.external_pages_token);

				NavigationUtils.navigate(history, `/anesthetist-questionary`);
			} else {
				NavigationUtils.navigate(history, `/scheduled-succesfully?token=${data.external_pages_token}`);
			}
		}
	}, [answers, selectedDate, appendAnswers, history, settings?.is_anesthetist_company, syncPatientFiles, setAnswer]);

	const handleSelectDate = useCallback(
		(date) => {
			setSelectedDate(date);
			const dateInfoSlots = slotsByDate.find((item) => item.date === date);
			setCurrentSlotsByUser(dateInfoSlots?.slotsByUser || []);
		},
		[slotsByDate]
	);

	const handleNavigateBackPress = useCallback(() => {
		clearEventSlotPlaceAndUser();
		NavigationUtils.navigate(history, '/select-event-group-payment-method');
	}, [clearEventSlotPlaceAndUser, history]);

	const handleSelectEvent = useCallback(
		async (eventId) => {
			const selectedEvent = scheduleSettings.events.find((item) => item.id === ~~eventId);
			setAnswer('event', selectedEvent);
		},
		[scheduleSettings.events, setAnswer]
	);

	const handleSelectPlace = useCallback(
		async (placeId) => {
			const selectedPlace = scheduleSettings.places.find((item) => item.id === ~~placeId);
			setAnswer('place', selectedPlace);
		},
		[scheduleSettings?.places, setAnswer]
	);

	return (
		<SelectTimegridForm
			loading={loading}
			loadingTimegrid={loadingTimegrid}
			selectedSlot={answers?.slot}
			listMaxHeight={listMaxHeight}
			selectedDate={selectedDate}
			variant={settings?.theme?.name}
			enabledDates={enabledDates}
			onSelectDate={handleSelectDate}
			currentSlotsByUser={currentSlotsByUser}
			onSubmitPress={handleSubmit}
			onSelectSlot={(slot) => setAnswer('slot', slot)}
			handleNavigateBackPress={handleNavigateBackPress}
			submitText={answers?.patient?.id ? 'Realizar agendamento' : 'Próximo'}
			patient={answers?.patient}
			selectedEventId={answers?.event?.id}
			onSelectEvent={handleSelectEvent}
			events={events}
			selectedPlaceId={answers?.place?.id}
			onSelectPlace={handleSelectPlace}
			places={scheduleSettings?.places}
			selectedPlace={answers?.place}
		/>
	);
}

export default SelectTimegridFormContainer;
